import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Plus, Target, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoalsInitiativesWidget = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    type: 'revenue',
    target_value: 0,
    level: 'company',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/director/goals`, {
        params: { status: 'active' }
      });
      setGoals(response.data);
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async () => {
    if (!newGoal.title || !newGoal.target_value) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/director/goals`, newGoal);
      toast.success('Goal created successfully');
      setCreateDialogOpen(false);
      setNewGoal({
        title: '',
        description: '',
        type: 'revenue',
        target_value: 0,
        level: 'company',
        start_date: '',
        end_date: ''
      });
      fetchGoals();
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toLocaleString()}`;
  };

  const calculateProgress = (current, target) => {
    if (!target) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const getGoalTypeLabel = (type) => {
    const labels = {
      'revenue': 'Revenue',
      'deals': 'Deals',
      'appointments': 'Appointments',
      'calls': 'Calls'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <>
      <div className="space-y-3">
        {/* Active Goals */}
        {goals.length > 0 ? (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {goals.map((goal, idx) => {
              const progress = calculateProgress(goal.current_value, goal.target_value);
              return (
                <div key={idx} className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{goal.title}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {getGoalTypeLabel(goal.type)} • {goal.level}
                      </div>
                    </div>
                    <Badge variant={progress >= 100 ? 'default' : progress >= 75 ? 'secondary' : 'outline'}>
                      {progress.toFixed(0)}%
                    </Badge>
                  </div>
                  
                  <Progress value={progress} className="h-2 mb-2" />
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>
                      {goal.type === 'revenue' ? formatCurrency(goal.current_value) : goal.current_value} achieved
                    </span>
                    <span>
                      Goal: {goal.type === 'revenue' ? formatCurrency(goal.target_value) : goal.target_value}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
            <Target className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No active goals</p>
            <p className="text-xs mt-1">Create goals to track progress</p>
          </div>
        )}

        {/* Create Button */}
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => setCreateDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Goal
        </Button>
      </div>

      {/* Create Goal Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Goal</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Goal Title *</Label>
              <Input
                placeholder="e.g., Q4 Revenue Target"
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the goal..."
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type *</Label>
                <Select value={newGoal.type} onValueChange={(value) => setNewGoal({ ...newGoal, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="deals">Deals</SelectItem>
                    <SelectItem value="appointments">Appointments</SelectItem>
                    <SelectItem value="calls">Calls</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Level *</Label>
                <Select value={newGoal.level} onValueChange={(value) => setNewGoal({ ...newGoal, level: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="market">Market</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="individual">Individual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Target Value *</Label>
              <Input
                type="number"
                placeholder="Enter target (e.g., 1000000)"
                value={newGoal.target_value}
                onChange={(e) => setNewGoal({ ...newGoal, target_value: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newGoal.start_date}
                  onChange={(e) => setNewGoal({ ...newGoal, start_date: e.target.value })}
                />
              </div>

              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={newGoal.end_date}
                  onChange={(e) => setNewGoal({ ...newGoal, end_date: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createGoal}>
              <Target className="h-4 w-4 mr-2" />
              Create Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoalsInitiativesWidget;